import { ApiClient } from './client';
import { splitNameParts } from '../utils/string';

export interface OAuthResponse {
  token_type: string;
  expires_in: number;
  access_token: string;
  refresh_token: string;
}

export interface OAuthData extends OAuthResponse {
  expiresAt: number;
}

export class ApiError extends Error {
  constructor(message?: string) {
    super(message);

    // Set the prototype explicitly, as per recommendations in the
    // breaking changes list for TypeScript 2.1
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

export class FetchClient implements ApiClient {
  public static REMEMBER_ME_KEY: string = `eop_user`;

  public oauthData?: OAuthData;
  public rememberMe?: boolean;
  private oauthDataReload: Promise<void> = this.reloadOauthData();

  public async getRaw<T, T2 = Partial<T>, T3 = T>(
    path: string,
    builder?: (properties: T2) => Promise<T3> | T3
  ): Promise<T3> {
    if (!builder) {
      throw new TypeError(`A builder function must be supplied.`);
    }

    const url = `${process.env.REACT_APP_API_URL}/${path}`;

    const response = await this.makeFetchRequest(url);
    const responseJson = await response.json();

    const data =
      responseJson && responseJson.data ? responseJson.data : responseJson;

    return await builder(data);
  }

  public async get<T, T2 = Partial<T>>(
    path: string,
    builder?: (properties: T2) => Promise<T> | T
  ): Promise<Array<T>> {
    if (!builder) {
      throw new TypeError(`A builder function must be supplied.`);
    }

    const url = `${process.env.REACT_APP_API_URL}/${path}`;

    const response = await this.makeFetchRequest(url);
    const responseJson = await response.json();

    const data =
      responseJson && responseJson.data ? responseJson.data : responseJson;

    if (Array.isArray(data)) {
      return Promise.all(data.map(builder));
    }

    return [await builder(data)];
  }

  public async login(
    username: string,
    password: string,
    rememberMe: boolean
  ): Promise<void> {
    await this.submitOauthTokenRequest(
      {
        grant_type: 'password',
        username,
        password
      },
      rememberMe
    );
  }

  public async signUp(
    username: string,
    password: string,
    name: string,
    contactPreference: boolean
  ): Promise<void> {
    const registerUrl = `${process.env.REACT_APP_API_URL}/user/register`;
    const { first, last } = splitNameParts(name);

    await this.makeJSONFetchRequest(registerUrl, {
      email: username,
      password,
      first_name: first,
      last_name: last,
      contact_me: contactPreference
    });
  }

  public async forgotPassword(email: string): Promise<void> {
    const forgotPasswordUrl = `${process.env.REACT_APP_API_URL}/user/forgotpassword`;

    await this.makeJSONFetchRequest(forgotPasswordUrl, { email });
  }

  public async resetPassword(
    token: string,
    email: string,
    password: string
  ): Promise<void> {
    const resetPasswordUrl = `${process.env.REACT_APP_API_URL}/user/resetpassword`;

    await this.makeJSONFetchRequest(resetPasswordUrl, {
      email,
      token,
      password,
      password_confirmation: password
    });
  }

  public async logout(ignoreFailure: boolean): Promise<void> {
    const logoutUrl = `${process.env.REACT_APP_API_URL}/oauth/logout`;

    try {
      await this.makeFetchRequest(logoutUrl);
      await this.flushOauthData();
    } catch (ex) {
      if (!ignoreFailure) {
        throw ex;
      }

      // HACK: Even if the request fails, support dumping local user data to
      // emulate a successful logout
      await this.flushOauthData();
    }
  }

  public async updateUser(
    name: string,
    colour: string,
    password?: string,
    currentPassword?: string
  ): Promise<void> {
    const updateUserUrl = `${process.env.REACT_APP_API_URL}/user/profile`;
    const { first, last } = splitNameParts(name);

    await this.makeJSONFetchRequest(
      updateUserUrl,
      {
        color: colour,
        first_name: first,
        last_name: last
      },
      'PUT'
    );

    if (!password || !currentPassword) {
      return;
    }

    await this.updatePassword(password, currentPassword);
    // TODO: If password in not falsy, then send a request to PUT user/password to update
  }

  public async updatePassword(
    password: string,
    currentPassword: string
  ): Promise<void> {
    const updatePasswordUrl = `${process.env.REACT_APP_API_URL}/user/password`;

    await this.makeJSONFetchRequest(
      updatePasswordUrl,
      {
        password,
        password_confirmation: password,
        old_password: currentPassword
      },
      'PUT'
    );
  }

  public async post(path: string, body?: Object): Promise<Response> {
    return this.makeJSONFetchRequest(
      `${process.env.REACT_APP_API_URL}/${path}`,
      body,
      'POST'
    );
  }

  public async put(path: string, body?: Object): Promise<Response> {
    return this.makeJSONFetchRequest(
      `${process.env.REACT_APP_API_URL}/${path}`,
      body,
      'PUT'
    );
  }

  public async delete(path: string, body?: Object): Promise<Response> {
    return this.makeJSONFetchRequest(
      `${process.env.REACT_APP_API_URL}/${path}`,
      body,
      'DELETE'
    );
  }

  private async makeJSONFetchRequest(
    url: string,
    data?: Object,
    method: string = 'POST'
  ): Promise<Response> {
    return this.makeFetchRequest(url, {
      body: data ? JSON.stringify(data) : undefined,
      headers: new Headers({
        'content-type': 'application/json; charset=UTF-8'
      }),
      method
    });
  }

  private async makeFetchRequest(
    input: Request | string,
    init?: RequestInit,
    refreshRequest: boolean = false
  ): Promise<Response> {
    const response = await fetch(
      input,
      await this.configureRequest(init, refreshRequest)
    );

    if (response.status >= 400) {
      try {
        const errorData = await response.json();

        if (!errorData || !errorData.errors) {
          throw new Error();
        }

        // Try to grab the first error from the errors object
        const firstKey = Object.keys(errorData.errors)[0];
        const firstSetOfErrors = errorData.errors[firstKey];

        if (!firstSetOfErrors || !firstSetOfErrors.length) {
          throw new Error();
        }

        throw new ApiError(firstSetOfErrors[0]);
      } catch (ex) {
        if (ex instanceof ApiError) {
          throw ex;
        }

        throw new ApiError();
      }
    }

    return response;
  }

  private async configureRequest(
    init?: RequestInit,
    refreshRequest: boolean = false
  ): Promise<RequestInit | undefined> {
    await this.oauthDataReload;

    if (!this.oauthData) {
      return init;
    }

    if (!refreshRequest) {
      await this.refreshAccessTokenIfRequired();
    }

    init = await this.attachAuthHeaderIfRequired(init);

    return init;
  }

  private async attachAuthHeaderIfRequired(
    init?: RequestInit
  ): Promise<RequestInit | undefined> {
    if (!this.oauthData) {
      return init;
    }

    init = init || {};
    if (!init.headers) {
      init.headers = new Headers();
    }

    (init.headers as Headers).append(
      'Authorization',
      `Bearer ${this.oauthData.access_token}`
    );

    return init;
  }

  private async refreshAccessTokenIfRequired(): Promise<void> {
    const BUFFER_TIME = 300 * 1000;

    if (!this.oauthData) {
      return;
    }

    const now = new Date();

    // If we have an expired (or nearly expired) token, then refresh it
    if (this.oauthData.expiresAt > now.getTime() + BUFFER_TIME) {
      return;
    }

    const { refresh_token } = this.oauthData;

    await this.submitOauthTokenRequest(
      {
        grant_type: 'refresh_token',
        refresh_token
      },
      this.rememberMe,
      true
    );
  }

  private async submitOauthTokenRequest(
    params: { [key: string]: string },
    rememberMe?: boolean,
    refreshRequest: boolean = false
  ): Promise<void> {
    const tokenUrl = `${process.env.REACT_APP_API_URL}/oauth/token`;
    const now = new Date();
    const requestTime = now.getTime();
    this.rememberMe = false;

    try {
      const response = await this.makeFetchRequest(
        tokenUrl,
        {
          body: JSON.stringify({
            ...params,
            scope: ''
          }),
          headers: new Headers({
            'content-type': 'application/json; charset=UTF-8'
          }),
          method: 'POST'
        },
        refreshRequest
      );

      const oauthData: OAuthResponse = await response.json();

      this.oauthData = {
        ...oauthData,
        expiresAt: requestTime + 1000 * oauthData.expires_in
      };

      if (rememberMe) {
        await this.persistOauthData();
        this.rememberMe = true;
      }
    } catch (ex) {
      // If we fail to refresh, then clear our local oauth data
      this.oauthData = undefined;

      if (this.rememberMe) {
        await this.persistOauthData();
      }
    }
  }

  private async persistOauthData(): Promise<void> {
    if (!window.localStorage) {
      return;
    }

    localStorage.setItem(
      FetchClient.REMEMBER_ME_KEY,
      JSON.stringify(this.oauthData)
    );
  }

  private async reloadOauthData(): Promise<void> {
    if (!window.localStorage) {
      return;
    }

    try {
      const dataString: string | null = localStorage.getItem(
        FetchClient.REMEMBER_ME_KEY
      );

      if (dataString) {
        this.oauthData = JSON.parse(dataString);
        // TODO: Implement max duration we'll keep auto-refreshing for before a
        // relogin is required (do this on backend?)
        this.rememberMe = true;
      }
    } catch (ex) {
      // Swallow any errors
    }
  }

  private async flushOauthData(): Promise<void> {
    if (!window.localStorage) {
      return;
    }

    localStorage.removeItem(FetchClient.REMEMBER_ME_KEY);
  }
}

export default FetchClient;



// WEBPACK FOOTER //
// ./src/app/api/fetchClient.ts